import type { LucideIcon } from "lucide-react";
import { Shield, ShieldAlert, ShieldX } from "lucide-react";
import type { SlideProps } from "@/engine/types";
import { GlassCard, Reveal, RevealItem, RevealList, SlideShell } from "@/ui";

interface HatSpec {
  name: string;
  icon: LucideIcon;
  lines: string[];
  /** Tailwind classes for the tint overlay, icon chip and title colour. */
  overlay: string;
  chip: string;
  iconColor: string;
  titleColor: string;
  dot: string;
  glow?: string;
}

const HATS: HatSpec[] = [
  {
    name: "White hat",
    icon: Shield,
    lines: [
      "Tactics aligned with search engine guidelines",
      "Sustainable for real brands and teams",
    ],
    overlay: "from-accent-emerald/15",
    chip: "bg-accent-emerald/15",
    iconColor: "text-accent-emerald",
    titleColor: "text-accent-emerald",
    dot: "bg-accent-emerald",
  },
  {
    name: "Grey hat",
    icon: ShieldAlert,
    lines: [
      "Borderline or short-term tricks",
      "Can work briefly — may backfire when policies or algorithms change",
    ],
    overlay: "from-accent-amber/15",
    chip: "bg-accent-amber/15",
    iconColor: "text-accent-amber",
    titleColor: "text-accent-amber",
    dot: "bg-accent-amber",
  },
  {
    name: "Black hat",
    icon: ShieldX,
    lines: [
      "Deliberate manipulation — cloaking, scaled spam…",
      "High risk of penalties, deindexing, and reputation loss",
    ],
    overlay: "from-accent-rose/15",
    chip: "bg-accent-rose/15",
    iconColor: "text-accent-rose",
    titleColor: "text-accent-rose",
    dot: "bg-accent-rose",
    glow: "0 0 72px -12px rgba(251, 113, 133, 0.45)",
  },
];

/** c6-hats — the three hats, defined in his words. */
export function HatsSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={6} title="White-hat, grey-hat, black-hat">
      <div className="flex w-full flex-col gap-12">
        <RevealList active={active} stagger={0.16} className="grid grid-cols-3 gap-8">
          {HATS.map((hat) => {
            const Icon = hat.icon;
            return (
              <RevealItem key={hat.name} className="h-full">
                <div
                  className="h-full rounded-2xl transition-transform duration-300 hover:-translate-y-1"
                  style={hat.glow ? { boxShadow: hat.glow } : undefined}
                >
                  <GlassCard className="relative h-full overflow-hidden p-10">
                    <div
                      className={`pointer-events-none absolute inset-0 bg-linear-to-b ${hat.overlay} via-transparent to-transparent`}
                    />
                    <div className="relative flex h-full flex-col gap-7">
                      <span
                        className={`inline-flex h-24 w-24 items-center justify-center rounded-2xl ${hat.chip}`}
                      >
                        <Icon size={52} className={hat.iconColor} strokeWidth={1.75} />
                      </span>
                      <h2
                        className={`font-display text-3xl font-semibold uppercase tracking-wide ${hat.titleColor}`}
                      >
                        {hat.name}
                      </h2>
                      <ul className="flex flex-col gap-4">
                        {hat.lines.map((line) => (
                          <li
                            key={line}
                            className="flex items-start gap-4 text-2xl leading-snug text-white/90"
                          >
                            <span className={`mt-3 h-2 w-2 shrink-0 rounded-full ${hat.dot}`} />
                            {line}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </GlassCard>
                </div>
              </RevealItem>
            );
          })}
        </RevealList>
        <Reveal active={active} delay={0.9}>
          <p className="text-center text-2xl text-white/80">
            Black hat:{" "}
            <span className="font-semibold text-accent-rose">not recommended</span> for
            production sites you care about.
          </p>
        </Reveal>
      </div>
    </SlideShell>
  );
}
